import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  api,
  APIError,
  isQuotaError,
  type AssistFieldKind,
  type ContentLang,
  type RewriteContext,
} from "@/api/client";
import CreditCost from "@/components/CreditCost";
import QuotaExhaustedCard from "@/components/QuotaExhaustedCard";

// Wiederverwendbarer KI-Assistenz-Button (B2): ✨ neben einem Textfeld öffnet
// ein Panel — Anweisung eingeben, Vorschlag holen, Alt/Neu vergleichen,
// übernehmen oder verwerfen. Der Call geht an /assist/rewrite; das
// Speichern bleibt beim Host (onApply → bestehender Feld-Save).
// Zwei Modi: mit courseId (Editor, Backend liest Kurs-Kontext aus der DB)
// oder kurs-los mit context + language (Wizard, stateless).

interface Suggestion {
  base: string; // Text zum Zeitpunkt des Calls (für die Alt-Ansicht)
  suggestion: string;
  rationale: string;
}

export default function AiAssistButton({
  courseId,
  fieldKind,
  getText,
  onApply,
  strategyHint,
  context,
  language,
  disabled,
}: {
  courseId?: string;
  fieldKind: AssistFieldKind;
  getText: () => string;
  onApply: (text: string) => void;
  strategyHint?: string;
  context?: RewriteContext;
  language?: ContentLang;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<unknown>(null);
  const [result, setResult] = useState<Suggestion | null>(null);

  async function fetchSuggestion() {
    const current = getText().trim();
    if (!current) {
      setError(t("assist.emptyField"));
      return;
    }
    if (!instruction.trim()) {
      setError(t("assist.emptyInstruction"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.assistRewrite({
        field_kind: fieldKind,
        current_text: current,
        instruction: instruction.trim(),
        ...(courseId
          ? { course_id: courseId }
          : { context: context ?? {}, language }),
      });
      setResult({
        base: current,
        suggestion: res.suggestion,
        rationale: res.rationale,
      });
    } catch (e) {
      if (isQuotaError(e)) {
        setQuota(e.data);
      } else {
        setError(
          e instanceof APIError
            ? e.status === 429
              ? t("common.aiLimit")
              : e.detail
            : t("assist.failed"),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setOpen(false);
    setResult(null);
    setError(null);
  }

  function apply() {
    if (!result) return;
    onApply(result.suggestion);
    close();
  }

  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        disabled={disabled}
        title={t("assist.title")}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-ca-accent text-ca-brand text-[10px] font-black uppercase tracking-wider shadow-sm hover:bg-brand-gold-dark hover:shadow-md disabled:bg-brand-cream-dark disabled:text-ca-ink-muted disabled:cursor-not-allowed disabled:shadow-none transition"
      >
        <span aria-hidden>✨</span>
        <span>{t("assist.buttonLabel")}</span>
        <CreditCost feature="assist" className="text-ca-brand/80" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-2 w-80 md:w-96 p-4 bg-ca-card rounded-xl border border-ca-surface-border shadow-2xl text-left normal-case tracking-normal">
          {strategyHint && (
            <p className="text-[10px] text-ca-surface-muted mb-3 leading-snug">
              <span className="font-bold uppercase tracking-wider">{t("assist.activeStrategy")} </span>
              {strategyHint}
            </p>
          )}

          {!result ? (
            <>
              <label className="block text-[10px] font-black uppercase tracking-widest text-ca-surface-muted mb-1">
                {t("assist.instructionLabel")}
              </label>
              <textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                placeholder={t("assist.instructionPlaceholder")}
                rows={2}
                className="w-full bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink text-sm outline-none focus:border-ca-accent resize-y placeholder:text-ca-surface-muted"
              />
              {quota && (
                <div className="mt-2">
                  <QuotaExhaustedCard detail={quota} onDismiss={() => setQuota(null)} />
                </div>
              )}
              {error && <p className="mt-2 text-xs text-verdict-red">{error}</p>}
              <div className="flex items-center gap-2 mt-3">
                <button
                  type="button"
                  onClick={fetchSuggestion}
                  disabled={busy}
                  className="px-3 py-1.5 rounded-md bg-ca-accent text-ca-brand text-xs font-black uppercase tracking-widest disabled:bg-brand-cream-dark disabled:text-ca-ink-muted disabled:cursor-not-allowed transition"
                >
                  {busy ? t("common.aiThinking") : t("assist.getSuggestion")}
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="text-xs text-ca-surface-muted hover:text-ca-surface-ink transition"
                >
                  {t("common.close")}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-ca-surface-muted mb-1">
                  {t("common.before")}
                </p>
                <p className="text-xs text-ca-surface-muted bg-white border border-ca-surface-border rounded-md px-3 py-2 whitespace-pre-wrap">
                  {result.base}
                </p>
              </div>
              <div className="mb-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-ca-surface-muted mb-1">
                  {t("assist.suggestion")}
                </p>
                <p className="text-xs text-ca-surface-ink bg-ca-accent/10 border border-ca-accent/40 rounded-md px-3 py-2 whitespace-pre-wrap">
                  {result.suggestion}
                </p>
                {result.rationale && (
                  <p className="mt-1 text-[11px] text-ca-surface-muted italic leading-snug">
                    → {result.rationale}
                  </p>
                )}
              </div>
              {error && <p className="mb-2 text-xs text-verdict-red">{error}</p>}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={apply}
                  className="px-3 py-1.5 rounded-md bg-ca-accent text-ca-brand text-xs font-black uppercase tracking-widest transition"
                >
                  {t("common.apply")}
                </button>
                <button
                  type="button"
                  onClick={() => setResult(null)}
                  className="px-3 py-1.5 rounded-md border border-ca-surface-border text-ca-surface-muted text-xs font-semibold hover:text-ca-surface-ink transition"
                >
                  {t("common.discard")}
                </button>
                <button
                  type="button"
                  onClick={fetchSuggestion}
                  disabled={busy}
                  className="text-xs text-ca-surface-accent hover:text-ca-accent disabled:text-ca-surface-muted transition"
                >
                  {busy ? t("common.aiThinking") : t("assist.retry")}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </span>
  );
}
